import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { signOut } from "aws-amplify/auth";
import { CircleUser, Menu } from "lucide-react";
import React from "react";
import {
  Route,
  BrowserRouter as Router,
  Routes,
  useLocation,
} from "react-router-dom";
import OrganizationSelector from "./components/OrganizationSelector";
import Sidebar from "./components/Sidebar";
import MainSystemPromptPage from "./pages/Admin/MainSystemPromptPage";
import OrganizationDetails from "./pages/Admin/OrganizationDetails";
import SimulationLab from "./pages/Admin/SimulationLab";
import TestGenPage from "./pages/Admin/TestGenPage";
import VerifiedPhoneNumbersPage from "./pages/Admin/VerifiedPhoneNumbersPage";
import CallPage from "./pages/CallPage";
import CallsPage from "./pages/CallsPage";
import Strategies from "./pages/Strategies";
import StrategyDetails from "./pages/StrategyDetails";
import SettingsPage from "./pages/SettingsPage";
import ChatInterface from "./pages/ChatbotDemo";
import { StyledAuthenticator } from "./components/StyledAuthenticator";
import { TooltipProvider } from "@/components/ui/tooltip";

// Create a layout component for the main dashboard
const DashboardLayout: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  return (
    <div className="grid h-screen w-screen bg-background lg:grid-cols-[280px_1fr] font-inter">
      <Sidebar />
      <div className="flex flex-col">
        <header className="flex h-14 lg:h-[60px] items-center gap-4 border-b bg-gray-100/40 px-6">
          <Sheet>
            <SheetTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="shrink-0 md:hidden"
              >
                <Menu className="h-5 w-5" />
                <span className="sr-only">Toggle navigation menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="flex flex-col p-0">
              <Sidebar />
            </SheetContent>
          </Sheet>
          <div className="flex-1">
            <OrganizationSelector />
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="rounded-full">
                <CircleUser className="h-5 w-5" />
                <span className="sr-only">Toggle user menu</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem>Support</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => signOut()}>
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>
        <main className="flex flex-1 flex-col gap-4 p-4 md:gap-8 md:p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

// Create a wrapper component to handle the layout logic
const AppContent: React.FC = () => {
  const location = useLocation();

  // Check if current route is the chat interface
  const isFullscreenRoute = location.pathname === "/demo/a213s3";

  if (isFullscreenRoute) {
    return <ChatInterface />;
  }

  return (
    <StyledAuthenticator>
      <DashboardLayout>
        <Routes>
          <Route path="/" element={<CallsPage />} />
          <Route path="/calls" element={<CallsPage />} />
          <Route path="/calls/:id" element={<CallPage />} />
          <Route path="/test-gen-basic" element={<SimulationLab />} />
          <Route path="/test-gen" element={<TestGenPage />} />
          <Route path="/strategies" element={<Strategies />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/strategy/:strategyId" element={<StrategyDetails />} />
          <Route path="/admin/organization" element={<OrganizationDetails />} />
          <Route path="/admin/numbers" element={<VerifiedPhoneNumbersPage />} />
          <Route
            path="/admin/main-system-prompt"
            element={<MainSystemPromptPage />}
          />
          <Route path="/customers" element={<Customers />} />
          <Route path="/analytics" element={<Analytics />} />
        </Routes>
      </DashboardLayout>
    </StyledAuthenticator>
  );
};

const App: React.FC = () => {
  return (
    <TooltipProvider>
      <Router>
        <Routes>
          <Route path="/demo/a213s3" element={<ChatInterface />} />
          <Route path="/*" element={<AppContent />} />
        </Routes>
      </Router>
    </TooltipProvider>
  );
};

const Customers: React.FC = () => (
  <div className="flex items-center">
    <h1 className="text-lg font-semibold md:text-2xl">Customers</h1>
  </div>
);

const Analytics: React.FC = () => (
  <div className="flex items-center">
    <h1 className="text-lg font-semibold md:text-2xl">Analytics</h1>
  </div>
);

export default App;
